import { Loading } from '@/components/loading.tsx'
import { Button } from '@/components/shadcn/button.tsx'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/shadcn/dialog.tsx'
import { Input } from '@/components/shadcn/input.tsx'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form.tsx'
import showToastNotification from '@/hooks/useShowToast.tsx'
import { trpc } from '@/utils/trpc'
import { zodResolver } from '@hookform/resolvers/zod'
import { CheckCircle2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocalStorage } from 'usehooks-ts'
import { z } from 'zod'

export const LoginFCA = ({
  open,
  onOpenChange,
  setShowSignupPrompt,
}: {
  open: boolean
  onOpenChange: () => void
  setShowSignupPrompt: (show: boolean) => void
}) => {
  const [message, setMessage] = useState<string | null>(null)
  const [isPending, setIsPending] = useState(false)
  const [, setUser] = useLocalStorage<{ name: string; email: string } | null>('fca_user', null)

  const { mutate: createFunnelCongruenceProfile, isPending: isCreatingFunnelCongruenceProfile } =
    trpc.createFunnelCongruenceProfile.useMutation({
      onSuccess: async () => {
        setUser({
          name: form.getValues('name'),
          email: form.getValues('email'),
        })
        setMessage('Access granted successfully!')
        showToastNotification('success', {
          message: 'Access granted successfully!',
          description: 'You can now access the Funnel Congruence Analysis.',
        })
        setTimeout(() => {
          onOpenChange()
        }, 4000)
      },
      onError: (error) => {
        if (error.message.toLowerCase() === 'profile already exists') {
          setShowSignupPrompt(true)
        } else {
          showToastNotification('error', {
            message: `Error.`,
            description: `${error.message}`,
          })
        }
      },
    })

  const formSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    email: z.string().email('Invalid email address'),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: '', email: '' },
  })

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    setIsPending(true)
    try {
      createFunnelCongruenceProfile(values)
    } catch (error) {
      showToastNotification('error', {
        message: 'Failed to save user details',
        description: `${error}`,
      })
    } finally {
      setIsPending(false)
    }
  }

  return (
    <Dialog open={open || isCreatingFunnelCongruenceProfile} onOpenChange={onOpenChange}>
      <DialogContent
        className="gap-6 bg-themebackground dark:bg-themebackgrounddark [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault()
        }}
      >
        <DialogHeader>
          <DialogTitle></DialogTitle>
        </DialogHeader>
        {message ? (
          <div className="flex flex-col items-center text-center gap-2 w-full h-full m-auto">
            <CheckCircle2 size="3rem" strokeWidth="1.2" color="green" />
            <p>{message}</p>
          </div>
        ) : isCreatingFunnelCongruenceProfile ? (
          <div className="flex flex-col gap-3 w-full h-full m-auto">
            <Loading />
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col items-center gap-2">
              <img alt={'CreativeOS'} src={'/images/logo-full-dark_mode.png'} className={'h-7  dark:block hidden'} />
              <img alt={'CreativeOS'} src={'/images/logo-full-light_mode.png'} className={'h-7 dark:hidden block'} />
              <p className="text-base font-medium text-center">
                Enter your name and email address below to gain access to the Funnel Congruence Analyzer.
              </p>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)}>
                <div className="flex flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium leading-5">Full Name</FormLabel>
                        <FormControl>
                          <Input className="shadow-none" placeholder="Enter your full name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium leading-5">Email Address</FormLabel>
                        <FormControl>
                          <Input className="shadow-none" placeholder="Enter your email address" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex justify-end gap-2">
                    <Button type="submit" className="w-15 px-3 h-8 text-sm" variant="default">
                      {isPending ? 'Submitting...' : 'Submit'}
                    </Button>
                  </div>
                </div>
              </form>
            </Form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
